/**
 * @file     searchValue.ts
 * @purpose  The filter value shape, its defaults and the status/type option
 *           lists. Kept apart from TraditionalSearchPanel so filter state,
 *           URL codecs and hero search can use them without pulling the
 *           panel's MUI form components into the page bundle.
 */

export type SortOrder =
  | 'newest'
  | 'price_asc'
  | 'price_desc'
  | 'beds_desc'
  /** Rentals only: earliest available date first. */
  | 'available_soonest';

export interface TraditionalSearchValue {
  /** Multi-select region filter — slugs from `src/core/config/regions.ts`
   *  (BCRES MLSAreaMajor sub-regions). */
  regions: string[];
  /** Multi-select neighbourhoods (mls_area_minor values). Finer than regions;
   *  seeded by the deal-finder location steps. */
  neighbourhoods: string[];
  /** Multi-select FSA / postal-prefix filter (e.g. "V6K"). Prefix-matched on
   *  postal_code. Selected on the map via the FSA overlay. */
  postalCodes: string[];
  priceMin?: number;
  priceMax?: number;
  /** Estimated monthly rent (StatCan / market rate / nearby lease comps). */
  rentMin?: number;
  rentMax?: number;
  bedsMin?: number;
  bathsMin?: number;
  propertyTypes: string[];
  livingAreaMin?: number;
  livingAreaMax?: number;
  yearBuiltMin?: number;
  yearBuiltMax?: number;
  lotSizeMin?: number;
  lotSizeMax?: number;
  parkingMin?: number;
  storiesMin?: number;
  openHouseOnly: boolean;
  /** Presale (pre-construction) listings only. Absent means off. */
  presaleOnly?: boolean;
  statuses: string[];
  sort: SortOrder;
  keywords: string[];
  address?: string;
  /** Selected pill ids from filterPills.PILLS (style + must-haves). */
  pills: string[];
  /** Street-number range (Paragon "Nelson St #100-500"), applied with `address`. */
  streetNumberFrom?: number;
  streetNumberTo?: number;
  /** Cross-street range ("4th Ave from Macdonald to Alma"). Map-only: resolved
   *  client-side from base-map geometry to a segment, not a server predicate. */
  crossStreetFrom?: string;
  crossStreetTo?: string;
  /** 'rent' searches MLS rentals (price fields mean monthly rent). Absent is the sale search. */
  mode?: 'rent';
  /** Rentals only: available on or before this date (YYYY-MM-DD). */
  availableBy?: string;
  /** Rentals only: the listing allows pets. */
  petsAllowed?: boolean;
  /** Rentals only: furnished. */
  furnishedOnly?: boolean;
  /** Rentals only: laundry in the unit. */
  inUnitLaundry?: boolean;
  /** Rentals only: short term (LeaseTerm "Short Term Lease") or long term. */
  leaseTerm?: 'short' | 'long';
}

export const DEFAULT_VALUE: TraditionalSearchValue = {
  regions: [],
  neighbourhoods: [],
  postalCodes: [],
  propertyTypes: [],
  openHouseOnly: false,
  statuses: ['Active'],
  sort: 'newest',
  keywords: [],
  address: '',
  pills: [],
};

export const PROPERTY_TYPES = [
  { label: 'House / Detached', value: 'detached' },
  { label: 'Condo / Apartment', value: 'condo' },
  { label: 'Townhouse', value: 'townhouse' },
  { label: 'Half Duplex', value: 'half-duplex' },
  { label: 'Duplex', value: 'duplex' },
  { label: 'Manufactured', value: 'manufactured' },
  { label: 'Multi-family', value: 'multi-family' },
  { label: 'Land / Lot', value: 'land' },
];

// Off-market options (VOW-gated: results require a verified VOW registration;
// the server answers 401/403 otherwise). "Terminated" maps to the feed's
// Canceled status — BCRES carries no Terminated value.
export const STATUSES = [
  { label: 'For sale', value: 'Active' },
  { label: 'Conditionally sold', value: 'ActiveUnderContract' },
  { label: 'Sold', value: 'Closed' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Expired', value: 'Expired' },
  { label: 'Terminated', value: 'Canceled' },
];

/** Status values that come from the VOW off-market set, not public IDX. */
export const OFF_MARKET_STATUS_VALUES = ['Closed', 'Pending', 'Expired', 'Canceled'];

export const statusLabel = (value: string): string =>
  STATUSES.find((s) => s.value === value)?.label ?? value;

// True only when a field that actually lives INSIDE the "More filters"
// disclosure is set. Drives whether the disclosure auto-expands. Deliberately
// excludes `sort` (always-visible dropdown) and `pills` (the chip rail) — those
// are commonly set and aren't in the disclosure, so they must not auto-expand
// it (e.g. on the map, where a non-default sort/pill was forcing it open).
export function hasTraditionalMoreFiltersActive(v: TraditionalSearchValue): boolean {
  return v.livingAreaMin != null || v.livingAreaMax != null ||
    v.rentMin != null || v.rentMax != null ||
    v.yearBuiltMin != null || v.yearBuiltMax != null ||
    v.lotSizeMin != null || v.lotSizeMax != null ||
    v.parkingMin != null || v.storiesMin != null ||
    v.openHouseOnly ||
    (v.statuses.length !== 1 || v.statuses[0] !== 'Active') ||
    (v.keywords?.length ?? 0) > 0 ||
    v.streetNumberFrom != null || v.streetNumberTo != null ||
    Boolean(v.crossStreetFrom?.trim()) || Boolean(v.crossStreetTo?.trim());
}
